import React, { useState, useEffect } from 'react'
import { withRouter, Link } from 'react-router-dom'
import { t } from '../../../i18n'
import Moment from 'moment'
import { CaretDown, CaretUp, UnfoldMore } from '../normalizer'
import { getStatusMeta } from './invoiceStatus'

const SortIcon = ({ direction, active }) => {
  if (!active) return <UnfoldMore className="data-table__sort-icon invoice__sort-icon" />
  return direction === 'asc'
    ? <CaretUp className="data-table__sort-icon data-table__sort-icon--active invoice__sort-icon invoice__sort-icon--active" />
    : <CaretDown className="data-table__sort-icon data-table__sort-icon--active invoice__sort-icon invoice__sort-icon--active" />
}

const SortableTh = ({ sortKey, label, sort, onSort, align = 'left' }) => {
  const isActive = sort.key === sortKey
  const alignClass = align === 'right' ? 'data-table__header--right' : align === 'center' ? 'data-table__header--center' : ''
  return (
    <th className={`data-table__header invoice__header ${alignClass}`} onClick={() => onSort(sortKey)}>
      <span className="data-table__header-content invoice__header-content">
        {label}
        <SortIcon direction={sort.direction} active={isActive} />
      </span>
    </th>
  )
}

const StaticTh = ({ label, align = 'left' }) => {
  const alignClass = align === 'right' ? 'data-table__header--right' : align === 'center' ? 'data-table__header--center' : ''
  return <th className={`data-table__header data-table__header--static invoice__header ${alignClass}`}>{label}</th>
}

// Renders a single column's cell value per its `type` (default 'text').
const renderCellValue = (column, invoice) => {
  const value = column.getValue(invoice)
  if (column.type === 'date') {
    return value ? new Moment(value).format(t('date-format')) : '-'
  }
  if (column.type === 'pill') {
    const statusMeta = getStatusMeta(invoice)
    return statusMeta
      ? <span className={'invoice-state-pill invoice-state-pill--' + statusMeta.tone}>{statusMeta.label}</span>
      : '-'
  }
  return value
}

const InvoiceTableHeader = ({ columns, sort, onSort, hasActions }) => (
  <thead>
    <tr className="invoice__table-header">
      {columns.map(column => (
        column.sortable === false
          ? <StaticTh key={column.key} label={column.label} align={column.align} />
          : <SortableTh key={column.key} sortKey={column.key} label={column.label} sort={sort} onSort={onSort} align={column.align} />
      ))}
      {hasActions && <StaticTh label="" align="center" />}
    </tr>
  </thead>
)

const InvoiceTableRow = ({ columns, invoice, linking, renderActions }) => {
  const invoicePath = `${linking}/${invoice.id}`
  return (
    <tr className="invoice__table-row">
      {columns.map(column => (
        <td key={column.key} className={`invoice__cell${column.align ? ' invoice__cell--' + column.align : ''}`}>
          {renderCellValue(column, invoice)}
        </td>
      ))}
      <td className="invoice__cell invoice__cell--center">
        {renderActions ? renderActions(invoice) : (
          <Link to={invoicePath} className="invoice__action">
            <i className="fas fa-eye" />
          </Link>
        )}
      </td>
    </tr>
  )
}

const InvoiceCardCell = ({ label, children }) => (
  <div className="data-table__card-cell">
    <span className="data-table__card-label">{label}</span>
    <span className="data-table__card-value">{children}</span>
  </div>
)

const InvoiceCard = ({ columns, invoice, linking, renderActions }) => {
  const invoicePath = `${linking}/${invoice.id}`
  return (
    <div className="data-table__card invoice__card">
      <div className="data-table__card-grid">
        {columns.map(column => (
          <InvoiceCardCell key={column.key} label={column.label}>
            {renderCellValue(column, invoice)}
          </InvoiceCardCell>
        ))}
      </div>
      <div className="data-table__card-actions">
        {renderActions ? renderActions(invoice) : (
          <Link to={invoicePath} className="data-table__card-action">
            <i className="fas fa-eye" />
            <span style={{ marginLeft: 8 }}>Ver detalhes</span>
          </Link>
        )}
      </div>
    </div>
  )
}

/**
 * InvoiceTable — renders an explicit, per-route set of columns.
 *
 * Props:
 *   columns       — array of column descriptors (see invoiceColumns.js). Required.
 *   invoices      — array of invoice objects to display.
 *   linking       — base path used to build the detail link (e.g. "/invoice/created").
 *   renderActions — optional (invoice) => node, overrides the default eye-icon
 *                   action cell/card-action (used by /invoice/created for Editar/Eliminar).
 */
const InvoiceTable = ({ columns, invoices, linking, renderActions }) => {
  const normalize = list => list.map(inv => ({
    ...inv,
    created_at: typeof inv.created_at === 'string' ? inv.created_at.replace('Z', '') : inv.created_at,
  }))
  const [invcs, setInvcs] = useState(normalize(invoices))
  const [sort, setSort] = useState({ key: null, direction: 'asc' })

  useEffect(() => { setInvcs(normalize(invoices)) }, [invoices]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sort.key) return
    const column = columns.find(c => c.key === sort.key)
    if (!column) return
    const isDate = column.type === 'date'
    setInvcs(prev => [...prev].sort((a, b) => {
      const dir = sort.direction === 'asc' ? 1 : -1
      if (isDate) {
        const aDate = new Moment(column.getValue(a))
        const bDate = new Moment(column.getValue(b))
        if (aDate.isBefore(bDate)) return -1 * dir
        if (aDate.isAfter(bDate))  return 1 * dir
        return 0
      }
      const aRaw = column.getValue(a)
      const bRaw = column.getValue(b)
      const aVal = aRaw == null ? '' : String(aRaw)
      const bVal = bRaw == null ? '' : String(bRaw)
      return aVal.localeCompare(bVal, undefined, { numeric: true }) * dir
    }))
  }, [sort]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key) => {
    setSort(prev => prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' })
  }

  return (
    <>
      {/* --list marks the list table: it is the only one swapped for cards
          below tablet, and the only one that needs the horizontal scroll its
          wrapper provides on tablet portrait. */}
      <div className="invoice__table-scroll">
        <table className="w-full invoice__table invoice__table--list">
          <InvoiceTableHeader columns={columns} sort={sort} onSort={handleSort} hasActions />
          <tbody>
            {invcs.map(i => (
              <InvoiceTableRow
                key={`invoice-table-td-${i.id}`}
                columns={columns}
                linking={linking}
                invoice={i}
                renderActions={renderActions}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="invoice__cards">
        {invcs.map(i => (
          <InvoiceCard
            key={`invoice-card-${i.id}`}
            columns={columns}
            linking={linking}
            invoice={i}
            renderActions={renderActions}
          />
        ))}
      </div>
      {invcs.length <= 0 && (
        <div className="text-center text-xl text-color pl-2 py-2">
          Sem faturas
        </div>
      )}
    </>
  )
}

export default withRouter(InvoiceTable)
